export interface YandexPlacemark {
  geometry: { setCoordinates: (coordinates: number[]) => void };
}

export interface YandexProjection {
  fromGlobalPixels: (point: number[], zoom: number) => number[];
  toGlobalPixels?: (coordinates: number[], zoom: number) => number[];
}

export interface YandexMapInstance {
  options: { get: (key: string) => unknown };
  geoObjects: { add: (object: object) => void; remove: (object: object) => void };
  setCenter: (center: number[], zoom?: number, options?: object) => void;
  getCenter: () => number[];
  getZoom: () => number;
  converter?: { pageToGlobal: (point: number[]) => number[] };
  container: { fitToViewport: () => void; getSize: () => number[] };
  destroy: () => void;
}

export interface YandexMapsAPI {
  ready: (onReady: () => void, onError?: (error: unknown) => void) => void;
  Map: new (container: HTMLElement, state: {
    center: number[];
    zoom: number;
    controls?: string[];
  }) => YandexMapInstance;
  Placemark: new (coordinates: number[], properties: object, options: object) => YandexPlacemark;
  Polygon: new (coordinates: number[][][], properties: object, options: object) => object;
}

declare global {
  interface Window {
    ymaps?: YandexMapsAPI;
  }
}

let mapsPromise: Promise<YandexMapsAPI> | null = null;
let failedAPI: YandexMapsAPI | undefined;

/** Reuse the address preloader and the delivery map's default SDK namespace. */
export function loadYandexMaps(): Promise<YandexMapsAPI> {
  if (mapsPromise) return mapsPromise;
  if (typeof window === 'undefined') return Promise.reject(new Error('Yandex Maps needs a browser'));

  // A namespace whose ready callback failed must not trap an explicit retry.
  if (failedAPI && window.ymaps === failedAPI) Reflect.deleteProperty(window, 'ymaps');
  failedAPI = undefined;

  mapsPromise = new Promise<YandexMapsAPI>((resolve, reject) => {
    let finished = false;
    let waitingForReady = false;
    let inspectedAPI: YandexMapsAPI | undefined;
    let script: HTMLScriptElement | undefined;
    const timers: { poll?: ReturnType<typeof setInterval>; timeout?: ReturnType<typeof setTimeout> } = {};

    const cleanup = () => {
      if (timers.poll) clearInterval(timers.poll);
      if (timers.timeout) clearTimeout(timers.timeout);
      script?.removeEventListener('load', inspect);
      script?.removeEventListener('error', fail);
    };
    const fail = () => {
      if (finished) return;
      finished = true;
      failedAPI = inspectedAPI;
      cleanup();
      script?.remove();
      reject(new Error('Yandex Maps could not be loaded'));
    };
    const inspect = () => {
      const api = window.ymaps;
      if (!api || waitingForReady || finished) return;
      waitingForReady = true;
      inspectedAPI = api;
      try {
        api.ready(() => {
          if (finished) return;
          if (!api.Map || !api.Polygon || !api.Placemark) {
            fail();
            return;
          }
          finished = true;
          cleanup();
          resolve(api);
        }, fail);
      } catch {
        fail();
      }
    };

    script = Array.from(document.scripts).find((candidate) => {
      if (!candidate.src.includes('api-maps.yandex.ru')) return false;
      const url = new URL(candidate.src);
      const ns = url.searchParams.get('ns');
      return url.pathname.startsWith('/2.1') && (!ns || ns === 'ymaps');
    });
    timers.timeout = setTimeout(fail, 15000);
    inspect();
    if (finished) return;

    if (!script && !window.ymaps) {
      script = document.createElement('script');
      const params = new URLSearchParams({
        apikey: process.env.NEXT_PUBLIC_YANDEX_MAPS_API_KEY || '',
        lang: 'ru_RU',
        load: 'package.full',
      });
      script.src = `https://api-maps.yandex.ru/2.1/?${params}`;
      script.async = true;
      script.addEventListener('load', inspect);
      script.addEventListener('error', fail);
      document.head.appendChild(script);
    } else {
      script?.addEventListener('load', inspect);
      script?.addEventListener('error', fail);
    }
    // A preloaded script can finish between the first inspection and listener.
    if (!finished) timers.poll = setInterval(inspect, 100);
  }).catch((error: unknown) => {
    mapsPromise = null;
    throw error;
  });
  return mapsPromise;
}
